import { Map as ImmutableMap } from 'immutable';
import { DateTime } from 'luxon';
import { createSelector } from 'reselect';

import type RootState from 'app-components/utils/redux-store/rootState';

import * as entitlementsTypes from './entitlementsTypes';

type EntitlementsState = Pick<RootState, entitlementsTypes.ENTITLEMENTS_REDUCER_KEY>;
type EntitlementsById = ImmutableMap<string, entitlementsTypes.Entitlement>;

const appTiers: { [key: string]: number | undefined } = {
  ACME_PROVISIONAL: 1,
  ACME_LITE: 2,
  ACME_BASIC: 3,
  ACME_PREMIER: 4,
  ACME_HAB: 5,
};

// Entitlements that are not app tiers have no rank.
const getAppTierRank = ({ id }: entitlementsTypes.Entitlement): number | undefined =>
  id && Object.prototype.hasOwnProperty.call(appTiers, id) ? appTiers[id] : undefined;

const getStore = (state: EntitlementsState): entitlementsTypes.EntitlementsStore =>
  state[entitlementsTypes.ENTITLEMENTS_REDUCER_KEY];
export const getLoadPending = (state: EntitlementsState): boolean => getStore(state).loadPending;
export const getError = (state: EntitlementsState): unknown => getStore(state).error;
export const getIsLoading = (state: EntitlementsState): boolean => getStore(state).isLoading;
export const getEntitlements = (state: EntitlementsState): EntitlementsById => getStore(state).resourcesById;
export const getResourcesById = getEntitlements;

export const getActiveEntitlements = (function createActiveEntitlementsSelector() {
  let activeEntitlementsCache = ImmutableMap<string, entitlementsTypes.Entitlement>();
  return (state: EntitlementsState): EntitlementsById => {
    const now = DateTime.local();
    const activeEntitlementsNew = getEntitlements(state).filter((entitlement) => {
      const partnerExpirationDate = entitlement.partnerExpireAt && DateTime.fromISO(entitlement.partnerExpireAt); // Set for only appstore/playstore subscriptions.
      // If there is partnerExpirationDate, only use that to check subscription status to support sandbox testing.
      if (partnerExpirationDate) {
        if (now <= partnerExpirationDate) {
          return true;
        }
        // 'expiresAt' is deprecated, but we have to use it for scenario when user switched from partner to e-store
        // "expiresAt": "2025-08-02T07:59:59Z",
        // "partnerExpireAt": "2019-11-17T00:00:00Z",
        // "expiresOn": "2025-08-01",
        if (entitlement.expiresAt) {
          return now <= DateTime.fromISO(entitlement.expiresAt);
        }
      }
      return Boolean(entitlement.active && now.startOf('day') <= DateTime.fromISO(entitlement.expiresOn ?? ''));
    });
    const cacheMatches = activeEntitlementsCache.equals(activeEntitlementsNew);
    if (!cacheMatches) {
      activeEntitlementsCache = activeEntitlementsNew;
    }
    return activeEntitlementsCache;
  };
})();

export const getActiveEntitlement = (
  state: EntitlementsState,
  key: string,
): entitlementsTypes.Entitlement | undefined => getActiveEntitlements(state).get(key);

export const getActiveTierEntitlements = createSelector(
  getActiveEntitlements,
  (activeEntitlements): EntitlementsById =>
    activeEntitlements.filter((entitlement) => getAppTierRank(entitlement) !== undefined),
);

export const getTopTierEntitlement = createSelector(
  getActiveTierEntitlements,
  (entitlements): entitlementsTypes.Entitlement | undefined => entitlements.sortBy(getAppTierRank).last(),
);

export const getBusinessEnabled = createSelector([getTopTierEntitlement], (entitlement): boolean =>
  Boolean(entitlement?.active && entitlement?.id === 'ACME_HAB'),
);

export const getIsQBP = getBusinessEnabled;
